import React, { useState, useEffect, useMemo, useCallback, Suspense } from "react";
import { securePost } from "../../config/api";
import { formatAmount } from "../../utils/format";
import LoadingLogo from "../../components/LoadingLogo";
import Rows from "../../components/Rows";
import closeIcon from "../../icons/x.svg";

const BarChart = React.lazy(() => import("react-chartjs-2").then(module => ({ default: module.Bar })));

const GraphModal = ({ graph, onClose, filters }) => {
  const [graphDetails, setGraphDetails] = useState(null);
  const [loading, setLoading] = useState(true);

  console.log("Graph Modal Props:", graph, filters);

  const abortController = useMemo(() => new AbortController(), []);
  const { signal } = abortController;

  const buildRequestBody = useCallback(() => {
    return { ...filters };
  }, [filters]);

  const fetchGraphDetails = useCallback(async () => {
    setLoading(true);
    const showSnackbar = () => {};
    try {
      const requestBody = buildRequestBody();
      console.log("Request Body:", requestBody);

      const response = await securePost(`/dashboard/detail/${graph.id}`, requestBody, showSnackbar, { signal });
      console.log("Graph Details Response:", response.data);
      setGraphDetails(response.data);
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(`Failed to fetch details for graph ${graph.id}:`, error);
      }
    } finally {
      setLoading(false);
    }
  }, [graph.id, signal, buildRequestBody]);

  useEffect(() => {
    if (graph) {
      fetchGraphDetails();
    }
    return () => {
      abortController.abort();
    };
  }, [graph, abortController, fetchGraphDetails]);

  if (!graph) return null;
  if (loading)
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-[1050]">
        <LoadingLogo />
      </div>
    );

  const { title, results } = graphDetails || graph;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-[1050]">
      <div className="bg-[#F9FAFC] p-8 rounded-lg shadow-lg max-w-5xl w-full relative min-h-[400px] ">
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-1 bg-white rounded-full text-gray-500 hover:text-gray-700"
        >
          <img src={closeIcon} alt="Close" className="w-6 h-6" />
        </button>
        <div className="flex items-center gap-2 mb-4">
          <h2 className="text-[32px] text-[#38485C] font-semibold">{title}</h2>
          <h2 className="text-[32px] text-[#6B78F8] font-semibold">({results.length})</h2>
        </div>

        <div className="w-full flex items-center justify-center ">
          {renderGraph(graphDetails || graph)}
        </div>
      </div>
    </div>
  );
};

const renderGraph = (graph) => {
  if (graph.type === "BAR_CHART") {
    return renderBarChart(graph);
  } else if (graph.type === "TABLE") {
    return renderTable(graph);
  }
  return <p className="text-gray-500">Unsupported Graph Type</p>;
};

const renderBarChart = (graph) => {
  const { title, configuration, results = [] } = graph;
  const barColor = configuration?.barChart?.color || "#007bff";
  const fillColor = configuration?.barChart?.fillColor || "#cce5ff";

  const labels = results.map((item) => item.x);
  const dataValues = results.map((item) => item.y);

  const data = {
    labels,
    datasets: [
      {
        label: title,
        data: dataValues,
        backgroundColor: fillColor,
        borderColor: barColor,
        borderWidth: 1,
        barPercentage: 0.8,
        categoryPercentage: 0.9,
        borderRadius: 8,

      },
    ],
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        backgroundColor: "rgba(0, 0, 0, 0.7)",
        titleColor: "#FFF",
        bodyColor: "#FFF",
        borderWidth: 1,
        borderColor: "#FFFFFF",
        padding: 10,
        displayColors: false,
        mode: "index",
        intersect: false,
        callbacks: {
          label: function (tooltipItem) {
            return `${tooltipItem.raw}`;
          },
          title: function () {
            return "";
          },
        },
      },
    },
    layout: { padding: 0 },
    scales: {
      x: {
        ticks: {
          color: "#38485C",
          font: {
            size: 12,
            family: "Montserrat",
            weight: 600,
          },
        },
        grid: { display: false },
        border: { display: false },
      },
      y: {
        beginAtZero: true,
        ticks: {
          color: "#38485C",
          font: {
            size: 12,
            family: "Montserrat",
            weight: 600,
          },
        },
        grid: { display: false },
        border: { display: false },
      },
    },
  };


  return (
    <div className="h-full w-full">
      <div className="h-[300px] w-full">
        <Suspense fallback={<div></div>}>
          <BarChart data={data} options={options} />
        </Suspense>      </div>
    </div>
  );
};

const renderTable = (graph) => {
  const { results, configuration } = graph;

  if (!results || results.length === 0) {
    return <p className="text-gray-500"></p>;
  }

  const columns = configuration?.table?.keys?.map((key) => ({
    key: key.name,
    label: key.name,
  })) || [];

  return (
    <div className="w-full flex flex-col">
      <div className="overflow-y-auto max-h-[400px]">
        <Rows
          columns={columns}
          data={results.map((row) =>
            Object.fromEntries(
              Object.entries(row).map(([key, value]) => [
                key,
                typeof value === "number" ? (
                  <span className="text-[#6B78F8] font-semibold">{formatAmount(value)}</span>
                ) : (
                  value
                ),
              ])
            )
          )}
          expandable={false}
        />
      </div>
    </div>
  );
};


export default GraphModal;
